/**
 * Weekly review: close the week, choose the next. What you finished, then — most urgent first — follow-ups you
 * missed, what slipped, decisions waiting on you, what your team owes you, what's waiting on track, next week's focus
 * and Someday; then save with a note to self. Every task sits in one section only.
 * Each decision is one click and takes effect at once.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { Ban, BellOff, Check, CheckCircle2, Clock, Gavel, Hourglass, Moon, Repeat, RotateCcw, Star, StarOff, Sun, CalendarDays } from "lucide-react";
import { useState } from "react";
import { api, post } from "../lib/api";
import { Card, Empty, ErrorNote, fmtDate, PageHeader } from "../components/ui";
import { ChaseButton } from "../components/Chase";
import { SubProgress } from "../components/work";
import { CategoryFilter, CategoryPill, matchCategory, type CategoryRef } from "../components/Categories";

interface Row { subtasks?: { total: number; done: number } | null; category?: CategoryRef | null; id: string; ref: string; title: string; priority: string; personal: boolean; dueAt: string | null; plannedDate: string | null; focusWeek: string | null; horizon: string | null; nextAction: string | null; repeat: unknown; spaceName: string; why?: string; assigneeNames?: string[]; quietDays?: number; lastChasedAt?: string | null; chaseCount?: number; followUpOn?: string | null; completedAt?: string; createdAt?: string; waitingOn?: { external?: string } | null }
interface Week {
  weekStart: string; weekEnd: string; nextWeekStart: string;
  stats: { done: number; missed?: number; slipped: number; decisions?: number; waiting: number; onTrack?: number; someday: number; created: number };
  done: Row[]; missed?: Row[]; slipped: Row[]; decisions?: Row[]; waiting: Row[]; onTrack?: Row[]; someday: Row[]; focus: Row[]; suggest: Row[];
  review: { notes: string; completed_at: string } | null;
}
type Action = "next_week" | "tomorrow" | "date" | "later" | "someday" | "drop" | "done" | "focus" | "unfocus" | "pull";

const d = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const WHY: Record<string, string> = { overdue: "Overdue", focus: "Was this week's focus", planned: "Planned, not done", waiting: "Waiting" };
/** The status at the start of a row's second line; red when something is late. */
const whyOf = (r: Row): { text: string; late: boolean } | null =>
  r.why === "missed" ? { text: `Follow-up missed · ${r.followUpOn ? d(r.followUpOn) : ""}`, late: true }
  : r.why === "decision" ? { text: `Decision needed${r.createdAt ? ` · waiting since ${d(r.createdAt.slice(0, 10))}` : ""}`, late: false }
  : r.why === "waiting" ? { text: `Waiting${r.waitingOn?.external ? ` on ${r.waitingOn.external}` : ""}${r.followUpOn ? ` · check back ${d(r.followUpOn)}` : ""}`, late: false }
  : r.why ? { text: WHY[r.why] ?? r.why, late: r.why === "overdue" } : null;

function Title({ r, wide }: { r: Row; wide?: boolean }) {
  return (
    <span className={clsx("min-w-0", wide ? "basis-full" : "flex-1")}>
      <span className="flex items-center gap-1.5"><Link to="/work/$id" params={{ id: r.id }} className="min-w-0 truncate text-sm font-medium hover:underline">{r.title}{r.repeat ? <Repeat size={11} className="muted ml-1 inline" /> : null}</Link>{r.category && <CategoryPill c={r.category} className="shrink-0" />}<SubProgress s={r.subtasks} /></span>
      <span className="muted block truncate text-xs">{whyOf(r) && <span className={whyOf(r)!.late ? "text-red-600" : undefined}>{whyOf(r)!.text}{" · "}</span>}{[r.dueAt && `due ${fmtDate(r.dueAt)}`, r.nextAction && `Next: ${r.nextAction}`, r.spaceName].filter(Boolean).join(" · ")}</span>
    </span>
  );
}

export function ReviewPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["review-week"], queryFn: () => api<Week>("/api/review/week") });
  const [gone, setGone] = useState<Record<string, string>>({}); // id → what happened
  const [focused, setFocused] = useState<Record<string, boolean>>({});
  const [dateFor, setDateFor] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [cat, setCat] = useState("");
  const [showOnTrack, setShowOnTrack] = useState(false);
  const w = q.data;

  const act = async (r: Row, action: Action, label: string, date?: string) => {
    setError(null);
    try {
      await post("/api/review/item", { itemId: r.id, action, date, weekStart: w?.weekStart });
      if (action === "focus") setFocused((f) => ({ ...f, [r.id]: true }));
      else if (action === "unfocus") setFocused((f) => ({ ...f, [r.id]: false }));
      else setGone((g) => ({ ...g, [r.id]: label }));
      setDateFor(null);
    } catch (e) {
      setError(e);
    }
  };
  const finish = async () => {
    if (!w) return;
    setSaving(true);
    try {
      await post("/api/review/complete", { weekStart: w.weekStart, notes: notes ?? w.review?.notes ?? "" });
      void qc.invalidateQueries();
      void navigate({ to: "/" });
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  if (q.isLoading || !w) return <Empty>{q.isLoading ? "Gathering your week…" : "Couldn't load the week."}</Empty>;

  const doneRow = (r: Row) => gone[r.id] && <span className="muted shrink-0 text-xs italic">{gone[r.id]}</span>;
  const btn = (r: Row, action: Action, label: string, icon: React.ReactNode, tone?: string) => (
    <button className={clsx("btn btn-ghost btn-sm !px-2", tone)} title={label} onClick={() => void act(r, action, label === "Done" ? "Done ✓" : `→ ${label}`)}>{icon}<span className="hidden sm:inline">{label}</span></button>
  );
  const rows = (list: Row[], extra?: (r: Row) => React.ReactNode) => (
    <ul className="divide-y divide-[var(--line)]">
      {list.filter(matchCategory(cat)).map((r) => (
        <li key={r.id} className={clsx("flex flex-wrap items-center gap-1 py-2", gone[r.id] && "opacity-50")}>
          <Title r={r} wide />
          {doneRow(r) || (
            <span className="-ml-2 flex flex-wrap items-center">
              {btn(r, focused[r.id] ? "unfocus" : "focus", focused[r.id] ? "Unstar" : "Focus", focused[r.id] ? <Star size={14} className="fill-amber-400 text-amber-500" /> : <Star size={14} />)}
              {btn(r, "tomorrow", "Tomorrow", <Sun size={14} />)}
              {btn(r, "next_week", "Next week", <CalendarDays size={14} />)}
              <button className="btn btn-ghost btn-sm !px-2" onClick={() => setDateFor(dateFor === r.id ? null : r.id)} title="Pick a date"><Clock size={14} /><span className="hidden sm:inline">Date</span></button>
              {btn(r, "someday", "Someday", <Moon size={14} />)}
              {btn(r, "done", "Done", <Check size={14} />, "text-emerald-700")}
              {btn(r, "drop", "Drop", <Ban size={14} />, "text-red-700")}
              {extra?.(r)}
            </span>
          )}
          {dateFor === r.id && <input type="date" className="input !w-auto !py-1 text-sm" autoFocus onChange={(e) => e.target.value && void act(r, "date", `→ ${d(e.target.value)}`, e.target.value)} />}
        </li>
      ))}
    </ul>
  );
  const missed = w.missed ?? [], decisions = w.decisions ?? [], onTrack = w.onTrack ?? [];
  const left = (l: Row[]) => l.filter((r) => !gone[r.id]).length;
  const focusList = [...w.focus.filter((r) => focused[r.id] !== false), ...[...missed, ...w.slipped, ...decisions, ...onTrack, ...w.someday, ...w.suggest].filter((r) => focused[r.id] && !w.focus.some((f) => f.id === r.id))]
    .filter((r, i, a) => a.findIndex((x) => x.id === r.id) === i);

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader eyebrow={`Week of ${d(w.weekStart)} – ${d(w.weekEnd)}`} title="Weekly review" subtitle="Ten minutes: close this week, choose the next."
        actions={w.review && <span className="chip text-xs text-emerald-700"><CheckCircle2 size={12} /> Reviewed {fmtDate(w.review.completed_at)}</span>} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[["Finished", w.stats.done, "text-emerald-700"], ["Missed follow-ups", missed.length, missed.length ? "text-red-600" : ""], ["Slipped", w.stats.slipped, w.stats.slipped ? "text-red-600" : ""], ["Decisions", decisions.length, decisions.length ? "text-amber-600" : ""], ["Others owe you", w.stats.waiting, ""], ["In Someday", w.stats.someday, ""]].map(([l, n, c]) => (
          <div key={l as string} className="rounded-xl border border-[var(--line)] bg-[var(--panel)] px-4 py-3"><div className={clsx("text-2xl font-bold", c as string)}>{n as number}</div><div className="muted text-xs">{l}</div></div>
        ))}
      </div>
      <ErrorNote error={error} />
      <CategoryFilter items={[...missed, ...w.slipped, ...decisions, ...w.waiting, ...onTrack, ...w.someday, ...w.suggest]} value={cat} onChange={setCat} />

      <Card title="1 · What you finished" icon={<CheckCircle2 size={16} />} count={w.done.length}>
        {w.done.length ? (
          <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">{(allDone ? w.done : w.done.slice(0, 12)).map((r) => <li key={r.id} className="flex items-center gap-2 truncate"><Check size={13} className="shrink-0 text-emerald-600" /><Link to="/work/$id" params={{ id: r.id }} className="truncate hover:underline">{r.title}</Link></li>)}</ul>
        ) : <p className="muted text-sm">Nothing ticked off this week yet.</p>}
        {w.done.length > 12 && <button className="btn btn-ghost btn-sm -ml-2 mt-1 text-brand-700" onClick={() => setAllDone((x) => !x)}>{allDone ? "Show fewer" : `Show all ${w.done.length}`}</button>}
      </Card>

      {missed.length > 0 && (
        <Card title="2 · Missed follow-ups" icon={<BellOff size={16} />} count={left(missed)} className="border-l-4 border-l-red-400">
          <p className="muted -mt-1 mb-2 text-xs">You were waiting on someone and the check-back date passed. Give each one a new home: the new day is also the new check-back date.</p>
          {rows(missed)}
        </Card>
      )}

      <Card title="3 · What slipped" icon={<RotateCcw size={16} />} count={left(w.slipped)} className={w.slipped.length ? "border-l-4 border-l-amber-400" : undefined}>
        <p className="muted -mt-1 mb-2 text-xs">Your own work, overdue or planned and not done. Give each one a new home. Star it to make it next week's focus.</p>
        {w.slipped.length ? rows(w.slipped) : <p className="muted text-sm">Nothing slipped. Good week.</p>}
      </Card>

      {decisions.length > 0 && (
        <Card title="4 · Decisions waiting on you" icon={<Gavel size={16} />} count={left(decisions)} className="border-l-4 border-l-amber-400">
          <p className="muted -mt-1 mb-2 text-xs">Marked “decision needed”. Decide, or give the decision a day.</p>
          {rows(decisions)}
        </Card>
      )}

      <Card title="5 · With your team" icon={<Hourglass size={16} />} count={w.waiting.length}>
        <p className="muted -mt-1 mb-2 text-xs">Handed out, overdue or quiet for 7+ days.</p>
        {w.waiting.length ? rows(w.waiting.map((r) => ({ ...r, why: undefined, nextAction: r.nextAction ?? `With ${r.assigneeNames?.join(", ") || "someone"}${r.quietDays ? ` · no update for ${r.quietDays} day${r.quietDays === 1 ? "" : "s"}` : ""}` })), (r) => <ChaseButton item={r as never} />)
          : <p className="muted text-sm">Nobody is late on anything you handed over.</p>}
      </Card>

      {onTrack.length > 0 && (
        <Card title="6 · Waiting, on track" icon={<Hourglass size={16} />} count={onTrack.length} actions={<button className="btn btn-ghost btn-sm text-brand-700" onClick={() => setShowOnTrack((x) => !x)}>{showOnTrack ? "Hide" : "Show"}</button>}>
          {showOnTrack ? rows(onTrack) : <p className="muted truncate text-xs">Nothing to do yet: {onTrack.slice(0, 4).map((r) => r.title).join(" · ")}{onTrack.length > 4 ? "…" : ""}</p>}
        </Card>
      )}

      <Card title={`7 · Next week's focus (${d(w.nextWeekStart)})`} icon={<Star size={16} />} count={focusList.length}>
        <p className="muted -mt-1 mb-2 text-xs">The 3–5 things that make next week a good week. They show on Today and in Plan my day all week.</p>
        {focusList.length > 0 && (
          <ul className="mb-3 space-y-1">
            {focusList.map((r) => (
              <li key={r.id} className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-1.5 text-sm">
                <Star size={14} className="shrink-0 fill-amber-400 text-amber-500" />
                <Link to="/work/$id" params={{ id: r.id }} className="min-w-0 flex-1 truncate font-medium hover:underline">{r.title}</Link>
                <SubProgress s={r.subtasks} />
                <button className="btn btn-ghost btn-sm !px-1.5" onClick={() => void act(r, "unfocus", "")} aria-label="Remove from focus"><StarOff size={14} /></button>
              </li>
            ))}
          </ul>
        )}
        {focusList.length > 6 && <p className="mb-2 text-xs text-amber-700">That's a lot for one week — fewer, done, beats more, half-done.</p>}
        {w.suggest.filter((r) => !focused[r.id] && !w.focus.some((f) => f.id === r.id)).length > 0 && (
          <>
            <h3 className="section-label mb-1">Suggestions</h3>
            <ul className="divide-y divide-[var(--line)]">
              {w.suggest.filter((r) => !focused[r.id] && !w.focus.some((f) => f.id === r.id)).filter(matchCategory(cat)).slice(0, 10).map((r) => (
                <li key={r.id} className="flex items-center gap-1 py-1.5">
                  <Title r={r} />
                  {r.priority === "P1" ? <span className="chip text-xs text-red-700">Urgent</span> : r.priority === "P2" ? <span className="chip text-xs">High</span> : null}
                  {btn(r, "focus", "Focus", <Star size={14} />)}
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      <Card title="8 · Someday — bring anything back?" icon={<Moon size={16} />} count={w.stats.someday}>
        {w.someday.length ? (
          <ul className="divide-y divide-[var(--line)]">
            {w.someday.filter(matchCategory(cat)).map((r) => (
              <li key={r.id} className={clsx("flex items-center gap-1 py-1.5", gone[r.id] && "opacity-50")}>
                <Title r={r} />
                {doneRow(r) || (
                  <span className="flex shrink-0">
                    {btn(r, focused[r.id] ? "unfocus" : "focus", focused[r.id] ? "Unstar" : "Focus", focused[r.id] ? <Star size={14} className="fill-amber-400 text-amber-500" /> : <Star size={14} />)}
                    {btn(r, "pull", "Bring back", <RotateCcw size={14} />)}
                    {btn(r, "drop", "Drop", <Ban size={14} />, "text-red-700")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : <p className="muted text-sm">Your Someday list is empty.</p>}
        {w.stats.someday > w.someday.length && <p className="muted mt-2 text-xs">Showing {w.someday.length} of {w.stats.someday}.</p>}
      </Card>

      <Card title="Note to self">
        <textarea className="input min-h-20 text-sm" placeholder="What went well, what to change next week…" value={notes ?? w.review?.notes ?? ""} onChange={(e) => setNotes(e.target.value)} />
      </Card>
      <div className="flex gap-2 pb-6">
        <button className="btn btn-primary flex-1" disabled={saving} onClick={() => void finish()}>{saving ? "Saving…" : w.review ? "Update this review" : "Finish the review"}</button>
        <Link to="/" className="btn">Later</Link>
      </div>
    </div>
  );
}
